import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { PopupListItem, RecordData, Service } from 'webapp-types';
import { PopupEvent, TemplateObjectType } from 'webapp/enums/enums';
import {
	arrayMove,
	generateWordsArray,
	isAlive,
	PopularLazyLoadSelector,
	sortArrayBasedOnSubset,
	sortBy,
} from 'webapp/utils/common';

import makeArray from 'webapp/helpers/make-array';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import SectionWithTogglerComponent from 'webapp/components/section-with-toggler';
import TemplatesAccountGridItemPlaceholderComponent from 'webapp/components/templates/account/grid-item-placeholder';
import TemplatesGridItemComponent from 'webapp/components/templates/grid-item';

import type { AddTagAction, DeleteAction } from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSortBoardAccountTemplate from 'webapp/models-custom/filter-sort/board-account-template';
import FilterSortReportAccountTemplate from 'webapp/models-custom/filter-sort/report-account-template';
import type Report from 'webapp/models-custom/report';
import type Template from 'webapp/models-custom/template';

interface Args {
	model: (Board | BoardLoop)[] | Report[]; // the board templates grid lists boards and loops together
	createTemplate: () => void;
	previewTemplate: (model: Template) => void;
	useTemplate: (model: Template) => void;
	editTemplate: (model: Board | Report | BoardLoop) => void;
	deleteTemplate: (model: Board | Report | BoardLoop) => void;
	openTemplatePermissions?: (model: Board | Report) => void;
}

type Action = AddTagAction | DeleteAction;

export default class TemplatesAccountGridComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;
	@service('store') declare storeService: Service.Store;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/report-templates')
	declare mmReportTemplatesService: Service.ModelManager.ReportTemplates;
	@service('model-manager/board-loop-templates')
	declare mmBoardLoopTemplatesService: Service.ModelManager.BoardLoopTemplates;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;

	@tracked otherSpacesAccountTemplatesSectionOpen = true;

	sortableEl: JQuery | null = null;
	filterSort: FilterSortBoardAccountTemplate | FilterSortReportAccountTemplate;
	visibleCheckContainerNode: HTMLElement | null = null;
	otherSpacesVisibleCheckContainerNode: HTMLElement | null = null;
	batchSelect: BatchSelect<Template>;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSortProperties = {
			selectors: PopularLazyLoadSelector.grid,
			getAllItems: () => {
				return this.allItems;
			},
			beforeFilter: () => {
				const typedFilter = this.filterSort?.typedFilter || '';

				this.allItems.forEach((item) => {
					// need to update activeFilter on template model because our highlighter
					// watches/matches string from template model, but we are using
					// board and report models for filter sort
					const template = item.templateModel;
					if (template) {
						template.activeFilter = generateWordsArray(typedFilter);
					}
				});
			},
			afterFilter: () => {
				// for visible-check - trigger on both container nodes
				const mainNode = this.visibleCheckContainerNode;
				if (mainNode) {
					this.triggerContentChange(mainNode);
				}

				const otherSpacesNode = this.otherSpacesVisibleCheckContainerNode;
				if (otherSpacesNode) {
					this.triggerContentChange(otherSpacesNode);
				}
			},
		};

		if (this.isBoardAccountTemplatesGrid) {
			this.filterSort = new FilterSortBoardAccountTemplate(
				filterSortProperties as FilterSortSettings<Board | BoardLoop>,
			);
		} else {
			this.filterSort = new FilterSortReportAccountTemplate(
				filterSortProperties as FilterSortSettings<Report>,
			);
		}

		this.batchSelect = new BatchSelect({
			getAllItems: () => {
				return this.filterSort.results
					.filter((i) => i.isForCurrentSpace)
					.map((item) => item.templateModel!);
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName?: string) => {
				return this.doBatchAction(actionName as Action);
			},
		});
	}

	get allItems() {
		return this.args.model;
	}

	get allTemplates(): Template[] {
		return this.allItems.map((item) => item.templateModel) as Template[];
	}

	get isBoardAccountTemplatesGrid(): boolean {
		return this.allItems[0] instanceof Board || this.allItems[0] instanceof BoardLoop;
	}

	get sortedItems() {
		return sortBy(this.allItems as (Board | Report | BoardLoop)[], 'order', 'id');
	}

	get accountTemplateItems() {
		return this.accountTemplateItemsFn(this.sortedItems);
	}

	get otherSpacesAccountTemplateItems() {
		return this.otherSpacesAccountTemplateItemsFn(this.sortedItems);
	}

	get showOtherSpacesAccountTemplatesSection(): boolean {
		return !!this.otherSpacesAccountTemplateItems.length;
	}

	get accountTypeText(): string {
		return this.accountService.isClient || this.accountService.hasPartnerResellerId
			? 'the organization'
			: 'other accounts';
	}

	get otherSpacesAccountTemplatesSectionTitle(): string {
		return `Templates from ${this.accountTypeText}`;
	}

	get otherSpacesAccountTemplatesSectionTooltipText(): string {
		return `These are templates from ${this.accountTypeText} that you have access to. They can only be edited where they were created.`;
	}

	get showPublicTemplateTileInCurrentSpaceTemplatesSection(): boolean {
		return (
			(!!this.accountTemplateItems.length || !this.otherSpacesAccountTemplateItems.length) &&
			this.accountService.showPublicTemplates
		);
	}

	get showPublicTemplateTileInOtherSpacesTemplatesSection(): boolean {
		return (
			!this.showPublicTemplateTileInCurrentSpaceTemplatesSection &&
			this.accountService.showPublicTemplates
		);
	}

	get addButtonContainerNode() {
		return isAlive(this) && !this.selectedItems.length
			? this.navigationService.addButtonContainerNode
			: null;
	}

	get batchButtonContainerNode() {
		return isAlive(this) && this.selectedItems.length
			? this.navigationService.batchButtonContainerNode
			: null;
	}

	get selectedItems(): Template[] {
		return this.batchSelect.selectedItems;
	}

	get batchOptions(): PopupListItem<Action>[] {
		const batchSelect = this.batchSelect;
		return [batchSelect.addTagsOption, batchSelect.deleteOption];
	}

	get hasBoards() {
		return this.selectedItems.some(
			(item) =>
				item.objectType === TemplateObjectType.BOARD ||
				item.objectType === TemplateObjectType.BOARD_LOOP,
		);
	}

	// account templates are boards / loops / reports that are templates, so they always have one
	templateOf(item: Board | Report | BoardLoop): Template {
		return item.templateModel!;
	}

	accountTemplateItemsFn(items: (Board | Report | BoardLoop)[]) {
		return items.filter((item) => item.isForCurrentSpace);
	}

	otherSpacesAccountTemplateItemsFn(items: (Board | Report | BoardLoop)[]) {
		return items.filter((item) => !item.isForCurrentSpace);
	}

	doBatchAction(actionName: Action) {
		return this[actionName]();
	}

	updateSortOrder(oldIndex: number, newIndex: number): void {
		if (!this.filterSort) {
			return;
		}

		const orderedIds = this.sortedItems.map((m) => m.templateId),
			filterOrderedIds = arrayMove(
				this.filterSort.results.map((m) => m.templateId),
				oldIndex,
				newIndex,
			);
		let newSortOrder = sortArrayBasedOnSubset(orderedIds, filterOrderedIds);

		// Cleanup template ids from other spaces (which are not sortable)
		const otherSpacesAccountTemplateIds = this.otherSpacesAccountTemplateItems.map(
			(item) => item.templateId,
		);
		newSortOrder = newSortOrder.filter((id) => !otherSpacesAccountTemplateIds.includes(id));

		this.requestService
			.api<RecordData.Payload>('PUT', '/templates/order', newSortOrder)
			.then((reply) => {
				this.storeService.pushPayload(reply);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));

		this.args.model.forEach((item) => {
			item.order = newSortOrder.indexOf(item.templateId);
			item.applyBufferedChanges(['order']); // we won't be sending save
		});
	}

	initSortable(node: HTMLElement): void {
		const ctEl = $(node),
			itemSelector = `${this.filterSort?.itemDomSelector}:not(.placeholder)`;

		ctEl.sortable({
			handle: '.drag',
			containment: 'parent',
			placeholder: {
				element: (/*currentItem*/) => {
					return $(
						'<div class="drop-placeholder flex-item"><div><ui-icon name="DragAndDropArea" size="xl" class="bounce"></ui-icon><span>Drop here</span></div></div>',
					);
				},
				update: () => {
					return;
				},
			},
			forcePlaceholderSize: true,
			tolerance: 'pointer',
			items: itemSelector,
			start: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				ui.item.find('.drag').attr('data-position', ui.item.index());
			},
			stop: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				const handle = ui.item.find('.drag'),
					oldIndex = Number(handle.attr('data-position')),
					newIndex = ui.item.index();

				if (oldIndex !== newIndex) {
					this.updateSortOrder(oldIndex, newIndex);
				}
			},
		});
	}

	destroySortable(): void {
		const el = this.sortableEl;
		if (el?.length && el.sortable('instance')) {
			el.sortable('destroy');
		}
	}

	batchUpdateBoardAndBoardLoopTags(): Promise<unknown> {
		const selectedItems = this.selectedItems;
		return new Promise((resolve, reject) => {
			let wasApplied = false;
			const applyFunc = (selectedTags: number[]) => {
				wasApplied = true;
				const promises: Promise<void>[] = [];

				const selectedBoards = selectedItems.filter(
					(item) => item.objectType === TemplateObjectType.BOARD,
				);

				promises.push(
					this.mmTemplatesService.batchUpdateTagsAction(selectedBoards, selectedTags),
				);
				promises.push(
					this.mmBoardLoopTemplatesService.batchUpdateBoardLoopTags(
						selectedItems,
						selectedTags,
					),
				);

				Promise.all(promises).then(resolve).catch(reject);
			};

			const popup = this.mmTagsService.openBatchTagsContext(applyFunc);

			if (popup) {
				popup?.on(PopupEvent.CLOSING, this, () => {
					if (!wasApplied) {
						reject();
					}
				});
			} else {
				reject();
			}
		});
	}

	batchDeleteBoardAndBoardLoops(): Promise<unknown> {
		const selectedItems = this.selectedItems;
		return new Promise<void>((resolve, reject) => {
			let wasSubmitted = false;
			const affirmativeFunc = async () => {
				wasSubmitted = true;
				const selectedBoards = selectedItems.filter(
					(item) => item.objectType === TemplateObjectType.BOARD,
				);

				try {
					await this.mmBoardLoopTemplatesService.batchDeleteBoardLoopTemplates(
						selectedItems,
					);
					await this.mmTemplatesService.batchDeleteAction(selectedBoards);

					resolve();
				} catch (error) {
					reject(error);
				}
			};

			const popup = this.mmTemplatesService.openBatchDeleteConfirmationPopup(() => {
				void affirmativeFunc();
			}, selectedItems.length);

			popup.on(PopupEvent.CLOSING, this, () => {
				if (!wasSubmitted) {
					reject();
				}
			});
		});
	}

	@action
	batchDelete() {
		const selectedItems = this.selectedItems;

		if (this.hasBoards) {
			return this.batchDeleteBoardAndBoardLoops();
		} else {
			return this.mmReportTemplatesService.batchDelete(selectedItems);
		}
	}

	@action
	batchUpdateTags() {
		if (this.hasBoards) {
			return this.batchUpdateBoardAndBoardLoopTags();
		} else {
			return this.mmReportTemplatesService.batchUpdateTags(this.selectedItems);
		}
	}

	@action
	toggleOtherSpacesAccountTemplatesSection(): void {
		this.otherSpacesAccountTemplatesSectionOpen = !this.otherSpacesAccountTemplatesSectionOpen;
	}

	@action
	setupView(node: HTMLElement): void {
		this.visibleCheckContainerNode = node;
		this.initSortable(node);
	}

	@action
	setOtherSpacesVisibleCheckContainerNode(node: HTMLElement): void {
		this.otherSpacesVisibleCheckContainerNode = node;
	}

	@action
	triggerOtherSpacesContentChange(): void {
		// for visible-check on other spaces section
		const node = this.otherSpacesVisibleCheckContainerNode;
		if (node) {
			this.triggerContentChange(node);
		}
	}

	@action
	teardownView(): void {
		this.destroySortable();
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}
	<template>
		<FilterLineProxyComponent @model={{this.filterSort}} />

		<FilterNoResultsComponent @model={{this.filterSort}} />

		<CommonListLazyLoadComponent
			@classNames="lazy-grid"
			@filterSort={{this.filterSort}}
			@filterExtraFn={{this.accountTemplateItemsFn}}
			@initialRowCount={{10}}
			@loadMoreRowCount={{5}}
			@animationProperty="id"
			@autoLoaded={{true}}
			@bottomDistance={{700}}
			as |visibleItems exporter|
		>

			<ul
				class="flex-ct flex-ct--size-board"
				data-hidden={{if this.filterSort.noResults "true" "false"}}
				{{didInsert this.setupView}}
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange visibleItems}}
				{{willDestroy this.teardownView}}
			>
				{{#each visibleItems as |item|}}
					<TemplatesGridItemComponent
						@model={{this.templateOf item}}
						@batchSelect={{this.batchSelect}}
						@targetModel={{item}}
						@onPreviewTemplate={{@previewTemplate}}
						@onUseTemplate={{@useTemplate}}
						{{!-- @hideSort={{exporter.hideSort}} --}}
						@hideSort={{true}}
						@onEditTemplate={{@editTemplate}}
						@onDeleteTemplate={{@deleteTemplate}}
						@onOpenTemplatePermissions={{@openTemplatePermissions}}
					/>
				{{/each}}

				{{#if this.showPublicTemplateTileInCurrentSpaceTemplatesSection}}
					<TemplatesAccountGridItemPlaceholderComponent
						@isBoardAccountTemplate={{this.isBoardAccountTemplatesGrid}}
					/>
				{{/if}}

				{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
				{{#each (makeArray length=exporter.itemsPerRow)}}
					<li class="flex-item flex-item--fake"></li>
				{{/each}}

			</ul>
			{{#unless exporter.areAllItemsLoaded}}
				<CommonListLoaderComponent />
			{{/unless}}
		</CommonListLazyLoadComponent>

		{{#if this.showOtherSpacesAccountTemplatesSection}}
			<SectionWithTogglerComponent
				@isOpen={{this.otherSpacesAccountTemplatesSectionOpen}}
				@title={{this.otherSpacesAccountTemplatesSectionTitle}}
				@tooltip={{this.otherSpacesAccountTemplatesSectionTooltipText}}
				@toggle={{this.toggleOtherSpacesAccountTemplatesSection}}
			>
				<CommonListLazyLoadComponent
					@classNames="lazy-grid"
					@filterSort={{this.filterSort}}
					@filterExtraFn={{this.otherSpacesAccountTemplateItemsFn}}
					@initialRowCount={{5}}
					@loadMoreRowCount={{5}}
					@animationProperty="id"
					@autoLoaded={{true}}
					@bottomDistance={{700}}
					as |visibleItems exporter|
				>

					<ul
						class="flex-ct flex-ct--size-board"
						data-hidden={{if this.filterSort.noResults "true" "false"}}
						{{visibleCheckContainer}}
						{{didInsert this.setOtherSpacesVisibleCheckContainerNode}}
						{{didUpdate this.triggerOtherSpacesContentChange visibleItems}}
					>
						{{#each visibleItems as |item|}}
							<TemplatesGridItemComponent
								@model={{this.templateOf item}}
								@batchSelect={{this.batchSelect}}
								@targetModel={{item}}
								@onPreviewTemplate={{@previewTemplate}}
								@onUseTemplate={{@useTemplate}}
							/>
						{{/each}}

						{{#if this.showPublicTemplateTileInOtherSpacesTemplatesSection}}
							<TemplatesAccountGridItemPlaceholderComponent
								@isBoardAccountTemplate={{this.isBoardAccountTemplatesGrid}}
							/>
						{{/if}}

						{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
						{{#each (makeArray length=exporter.itemsPerRow)}}
							<li class="flex-item flex-item--fake"></li>
						{{/each}}

					</ul>
					{{#unless exporter.areAllItemsLoaded}}
						<CommonListLoaderComponent />
					{{/unless}}
				</CommonListLazyLoadComponent>
			</SectionWithTogglerComponent>
		{{/if}}

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent @text="New template" @callTrigger={{@createTemplate}} />
			{{/in-element}}
		{{/if}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
